import React, { useState } from 'react';
import { useToggle } from 'react-use';
import { useHistory, useLocation } from 'react-router-dom';
import Bugsnag from '@bugsnag/js';
import ReCAPTCHA from "react-google-recaptcha";

import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/ButtonBase';
import TextField from '@material-ui/core/TextField';

/*Class Prefix = auth-form*/
import './styles.css';

import firebase from '../../Firebase';

import logger from '../../utils/logger';
import GOOGLE_SIGN_IN_BUTTON from '../../assets/btn_google_signin_light_normal_web@2x.png';
import * as ROUTES from '../../constants/routes';
import Spinner from '../../components/Spinner';
import Mixpanel, { EVENTS } from '../../utils/mixpanel';

const AuthForm = function(props) {

	const [signIn, toggleSignInSignUp] = useToggle(props.defaultTo === 'signin');
	const history = useHistory();
	const query = new URLSearchParams(useLocation().search);

	const [email, setEmail] = useState('');
	const [firstName, setFirstName] = useState('');
	const [password, setPassword] = useState('');
	const [verifiedPassword, setVerifiedPassword] = useState('');
	const [errorMessage, setErrorMessage] = useState(null);
	const [recaptchaVerified, setRecaptchaVerified] = useState(false);

	const [awaiting, setAwaiting] = useState(false);
	const [submissionError, setSubmissionError] = useState(false);

	const handleInput = (setFunc) => (e) => setFunc(e.target.value);

	const handleOpenPrivacyPolicy = (e) => {
		e.stopPropagation();
		e.preventDefault();
		window.open(`${window.location.origin}/privacy-policy`, '_blank');
	}

	const handleGoogleSubmit = () => {
		setAwaiting(true);
		let promise = firebase.doGoogleSignIn()
		promise.then((result) => {
			logger.log("Signed in with Google");
			setAwaiting(false);	// no longer waiting
			Mixpanel.track(EVENTS.signIn);
			query.delete('redirect');
			history.push({
				pathname: query.get('redirect') || ROUTES.HOME,
				search: query.toString(),
			});
		}).catch((e) => {
			logger.error("Error signing in: ", e)
			setAwaiting(false);	// no longer waiting
			setSubmissionError(true);
			Bugsnag.notify(e);
		});
	}

	const handleSignIn = (e) => {
		e.preventDefault();
		setAwaiting(true);
		// since firebase is signing in the onAuthStateChange callback will fire and cause the user's data
		//		to be loaded in the App/index.js so no need to handle loading it here
		firebase.doSignInWithEmailAndPassword(email, password).then((response) => {
			Mixpanel.track(EVENTS.signIn, { context: props.context || 'none' });
			setAwaiting(false);	// no longer waiting
			query.delete('redirect');
			history.push({
				pathname: query.get('redirect') || ROUTES.HOME,
				search: query.toString(),
			});
		}).catch((e) => {
			logger.error("Error signing in: ", e)
			setAwaiting(false);	// no longer waiting
			// handle various sign in errors
			switch (e.code) {
				case "auth/user-not-found": 	// this means the email is unrecognized
					// reset state variables
					setPassword("");
					setVerifiedPassword("")
					setErrorMessage('this user does not exist')
					break;
					case "auth/wrong-password": 	// this means they fucked up their password
					// reset state variables
					setPassword("");
					setVerifiedPassword("");
					setErrorMessage('incorrect password')
					break;
				default:
					setErrorMessage('something broke, please try again later');	// general error, we don't know what happened exactly
					Bugsnag.notify(e);	// if not expected report it
			}
		});
	}

	const handleSignUp = (e) => {
		e.preventDefault();
		setAwaiting(true);
		// handleTrackAdwordsConversion();	// called in App.js
		// first create the user in AUTH
		firebase.doCreateUserWithEmailAndPassword(email, password, firstName).then((cred) => {
			// then add the user to FIRESTORE
			logger.log("user created in auth: ", cred)
			// return firebase.saveNewUserToFirestore(cred.user.uid, email, firstName, lastName);	// this now gets handled in App.js I think
			return Promise.resolve();
		}).then((resp) => {
			setAwaiting(false);
			// logger.log("User successfully created and added to database, redirecting to Courses...");
			// dispatch(createProfileSuccess(email, firstName));	// update redux
			// Mixpanel.track(EVENTS.signUp, { context: props.context || 'none' });	// tracked in App.js
			query.delete('redirect');
			history.push({
				pathname: query.get('redirect') || ROUTES.HOME,
				search: query.toString(),
			});
		}).catch((e) => {
			setAwaiting(false);
			setErrorMessage('an unknown error occurred, please try agan later');
			logger.error("Error creating user: ", e);
			Bugsnag.notify(e);
		})
	}

	const buttonIsDisabled = signIn ? (email.length === 0 || password.length === 0)
	: (firstName.length === 0 || email.length === 0 || password.length === 0 || !recaptchaVerified)

	if (submissionError) {
		return <p>There was an error, please refresh and/or try again later</p>
	} else if (awaiting) {
		return <div className="auth-form-root" ><Spinner /></div>
	}

	return (
		<div className="auth-form-root" >
			<p className="auth-form-header" >
				{signIn ? 'Sign In' : 'Sign Up'}
			</p>
			<p>
				{signIn ? 'Don\'t have an account yet?' : 'Already have an account'}
				&nbsp;click <button className="auth-form-alternate-button" onClick={toggleSignInSignUp} >here</button>
			</p>
			<form
				className="auth-form-form-container"
				onSubmit={signIn ? handleSignIn : handleSignUp}
			>
				{!signIn &&
					<TextField
						value={firstName}
						className="auth-form-input"
						placeholder="Name"
						label="name"
						variant='outlined'
						type="name"
						onChange={handleInput(setFirstName)}
					/>
				}
				<TextField
					value={email}
					variant='outlined'
					label='email'
					type='email'
					className="auth-form-input"
					onChange={handleInput(setEmail)}
				/>
				<TextField
					value={password}
					className="auth-form-input"
					variant='outlined'
					label="password"
					placeholder="password"
					type="password"
					onChange={handleInput(setPassword)}
				/>

				{!signIn &&
					<ReCAPTCHA
						sitekey={process.env.NODE_ENV === 'development' ?
							process.env.REACT_APP_RECAPTCHA_SITE_KEY_DEVELOPMENT : process.env.REACT_APP_RECAPTCHA_SITE_KEY_PRODUCTION}
						onChange={() => setRecaptchaVerified(true)}
					/>
				}

				{!signIn && false &&
					<TextField
						variant='outlined'
						value={verifiedPassword}
						className="auth-form-input"
						placeholder="confirm password"
						type="password"
						onChange={handleInput(setVerifiedPassword)}
					/>
				}
				{errorMessage && <div className='auth-error' >{errorMessage}</div>}
				<Button
					variant='contained'
					type="submit"
					color='secondary'
					size='large'
					disabled={buttonIsDisabled}
				>
					{signIn ? 'Sign In' : 'Sign Up'}
				</Button>
				<a href="/pw-forget" className="auth-form-alternate-button" >forgot password</a>
			</form>
			<div className="flex-column-center" >
				<p>or</p>
				<ButtonBase
					className="auth-form-google-button"
					onClick={handleGoogleSubmit}
				>
					<img alt='google-sign-in-button' src={GOOGLE_SIGN_IN_BUTTON} />
				</ButtonBase>
			</div>
			<p style={{ marginTop: 10 }} >
				By clicking&nbsp;{signIn ? '"Sign In"' : '"Sign Up"'}&nbsp;you agree to our
				<a onClick={handleOpenPrivacyPolicy} href="/privacy-policy" >&nbsp;terms of service and privacy policy</a>
			</p>
		</div>
	);
}

export default AuthForm;